import { isRecord } from '@/lib/api'
import type {
  PreferredCounterEdit,
  PreferredCounterPolicy,
} from '../api/preferred-counter-policy-api'
import type {
  EventRuleContent,
  EventRulePolicyEdit,
  PrinterEventRulePolicy,
} from '../api/event-rule-policies-api'

export type PolicyFieldChoice = 'yours' | 'latest'
export type PolicyFieldComparison<T> = {
  opened: T
  yours: T
  latest: T
  status: 'unchanged' | 'yours' | 'latest' | 'identical' | 'conflict'
}

function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) return true
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length
      && left.every((value, index) => sameValue(value, right[index]))
  }
  if (!isRecord(left) || !isRecord(right)) return false
  const keys = Object.keys(left)
  return keys.length === Object.keys(right).length
    && keys.every((key) => Object.hasOwn(right, key) && sameValue(left[key], right[key]))
}

function sameCounters(left: string[], right: string[]): boolean {
  const selected = new Set(left)
  return selected.size === new Set(right).size && right.every((counter) => selected.has(counter))
}

function compareField<T>(
  opened: T,
  yours: T,
  latest: T,
  equal: (left: T, right: T) => boolean = sameValue,
): PolicyFieldComparison<T> {
  const yoursChanged = !equal(opened, yours)
  const latestChanged = !equal(opened, latest)
  let status: PolicyFieldComparison<T>['status'] = 'unchanged'
  if (yoursChanged && latestChanged) status = equal(yours, latest) ? 'identical' : 'conflict'
  else if (yoursChanged) status = 'yours'
  else if (latestChanged) status = 'latest'
  return { opened, yours, latest, status }
}

function needsChoice(field: PolicyFieldComparison<unknown>, choice?: PolicyFieldChoice) {
  return field.status === 'conflict' && choice === undefined
}

function resolvedField<T>(field: PolicyFieldComparison<T>, choice?: PolicyFieldChoice): T {
  if (field.status === 'yours' || field.status === 'identical') return field.yours
  if (field.status === 'conflict' && choice === 'yours') return field.yours
  return field.latest
}

function selector<T>(edit: { selectorExpression: string | null; selectorGui?: T }) {
  const expression = edit.selectorExpression?.trim() ? edit.selectorExpression : null
  return { selectorExpression: expression, selectorGui: expression ? edit.selectorGui ?? null : null }
}

export function comparePreferredCounterPolicy(
  opened: PreferredCounterPolicy,
  yours: PreferredCounterEdit,
  latest: PreferredCounterPolicy,
) {
  return {
    latest,
    fields: {
      name: compareField(opened.name.trim(), yours.name.trim(), latest.name.trim()),
      selector: compareField(selector(opened), selector(yours), selector(latest)),
      formatPriority: compareField(opened.value.formatPriority, yours.value.formatPriority, latest.value.formatPriority),
      countersToDisplay: compareField(opened.value.countersToDisplay, yours.value.countersToDisplay, latest.value.countersToDisplay, sameCounters),
    },
  }
}

export type PreferredCounterComparison = ReturnType<typeof comparePreferredCounterPolicy>
export type PreferredCounterChoices = Partial<Record<keyof PreferredCounterComparison['fields'], PolicyFieldChoice>>

export function resolvePreferredCounterPolicy(
  comparison: PreferredCounterComparison,
  choices: PreferredCounterChoices,
): PreferredCounterEdit | null {
  const fields = comparison.fields
  if (needsChoice(fields.name, choices.name)
    || needsChoice(fields.selector, choices.selector)
    || needsChoice(fields.formatPriority, choices.formatPriority)
    || needsChoice(fields.countersToDisplay, choices.countersToDisplay)) return null

  return structuredClone({
    name: resolvedField(fields.name, choices.name),
    ...resolvedField(fields.selector, choices.selector),
    value: {
      formatPriority: resolvedField(fields.formatPriority, choices.formatPriority),
      countersToDisplay: resolvedField(fields.countersToDisplay, choices.countersToDisplay),
    },
  })
}

function condition(content: EventRuleContent) {
  return { detectorType: content.detectorType, configuration: content.configuration }
}

function notificationActions(actions: EventRuleContent['actions']) {
  return actions.map((action) => ({
    ...action,
    recipients: action.recipients.map((recipient) => ({
      ...recipient,
      id: recipient.id ?? null,
      address: recipient.address ?? null,
    })),
  }))
}

export function compareEventRulePolicy(
  opened: PrinterEventRulePolicy,
  yours: EventRulePolicyEdit,
  latest: PrinterEventRulePolicy,
) {
  return {
    latest,
    fields: {
      name: compareField(opened.name.trim(), yours.name.trim(), latest.name.trim()),
      selector: compareField(selector(opened), selector(yours), selector(latest)),
      condition: compareField(condition(opened.rule.content), condition(yours.content), condition(latest.rule.content)),
      actions: compareField(opened.rule.content.actions, yours.content.actions, latest.rule.content.actions,
        (left, right) => sameValue(notificationActions(left), notificationActions(right))),
    },
  }
}

export type EventRuleComparison = ReturnType<typeof compareEventRulePolicy>
export type EventRuleChoices = Partial<Record<keyof EventRuleComparison['fields'], PolicyFieldChoice>>

export function resolveEventRulePolicy(
  comparison: EventRuleComparison,
  choices: EventRuleChoices,
): EventRulePolicyEdit | null {
  const fields = comparison.fields
  if (needsChoice(fields.name, choices.name)
    || needsChoice(fields.selector, choices.selector)
    || needsChoice(fields.condition, choices.condition)
    || needsChoice(fields.actions, choices.actions)) return null

  return structuredClone({
    name: resolvedField(fields.name, choices.name),
    ...resolvedField(fields.selector, choices.selector),
    content: {
      ...resolvedField(fields.condition, choices.condition),
      actions: resolvedField(fields.actions, choices.actions),
    },
    overridesPolicyId: comparison.latest.overridesPolicyId,
  })
}
